// @ts-check
import Tooltip from '@material-ui/core/Tooltip';
import { getColor } from 'common/constants/_colors';
import Icon from 'components/base/Icon';
import { Flex, Margin } from 'components/base/layout-components';
import Text from 'components/base/Text';
import NotificationList from 'components/Notification/NotificationList';
import NotificationPlaceHolderList from 'components/Notification/NotificationPlaceholder';
import ComingSoonLightBulb from 'images/coming-soon-light-bulb.svg';
import NoNews from 'images/happy-bell.svg';
import styled from 'styled-components';
import { redirectToTeamGoals } from 'common/utils/utils';
import withPermissions from 'connected-components/HOC/PermissionsHOC';
import { CAN_EDIT_ACCOUNT_TYPES } from '@linearb/linjs-common';
import { StyledWidgetHeader } from 'components/base/common-styles';
import { LBArrowCornerRightUpIcon, LBButton } from '@linearb/buzz';

const Title = styled(Flex)`
  border-bottom: 1px solid ${getColor('catskillWhite')};
  padding: 2rem;
  align-items: center;
`;

const ComingSoonImageStyle = styled.img`
  height: 11.3rem;
  width: 9.45rem;
`;

const ImageWrapper = styled(Flex)`
  width: 100%;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const NewWrapper = styled(Flex)`
  background-color: ${getColor('dodgerBlue')};
  border-radius: 3rem;
  align-items: center;
`;

const MAX_NEW_NOTIFICATIONS = 20;
const New = ({ amount }) => (
  <NewWrapper pl='small' pr='small' pt='tiny' pb='tiny'>
    <Text color='white'>{`${
      amount > MAX_NEW_NOTIFICATIONS ? `${MAX_NEW_NOTIFICATIONS}+` : amount
    } NEW`}</Text>
  </NewWrapper>
);

const ComingSoon = () => (
  <ImageWrapper>
    <ComingSoonImageStyle alt='Coming Soon' src={ComingSoonLightBulb} />
    <Text pt='2.4rem' size='x-x-large' color='melrose'>
      Notifications coming soon!
    </Text>
  </ImageWrapper>
);

const EmptyNotifications = () => (
  <ImageWrapper>
    <img alt='No News' src={NoNews} />
    <Text pt='2.4rem' size='x-x-large' color='shuttleGray'>
      No news is good news
    </Text>
    <Text pt='1rem' color='osloGray' size='medium'>
      We’ll notify you about things that need your attention
    </Text>
    <Text pt='0.5rem' color='osloGray' size='medium'>
      like pull requests that are waiting to be reviewed and more
    </Text>
  </ImageWrapper>
);

const NotificationsContent = ({
  notifications,
  onRemoveClick,
  isLoading,
  isComingSoon
}) => {
  if (isComingSoon) {
    return <ComingSoon />;
  }
  if (isLoading) {
    return <NotificationPlaceHolderList />;
  }
  return notifications.length ? (
    <NotificationList
      notifications={notifications.slice(0, MAX_NEW_NOTIFICATIONS)}
      onRemoveClick={onRemoveClick}
    />
  ) : (
    <EmptyNotifications />
  );
};

const Notifications = ({
  notifications = [],
  onRemoveClick,
  newNotifications,
  isLoading,
  isComingSoon,
  slackChannel,
  history,
  selectedTeamDashboardId,
  selectedTeam
}) => {
  return (
    <Flex
      direction='column'
      width='100%'
      height='100%'
      data-cy='goals-notification-widget'
    >
      <Title>
        <Flex grow='1'>
          <StyledWidgetHeader>Goals Notifications</StyledWidgetHeader>
          {!!newNotifications && (
            <Margin l={'small'}>
              <New amount={newNotifications} />
            </Margin>
          )}
        </Flex>

        {selectedTeamDashboardId && selectedTeam && !isComingSoon ? (
          <Tooltip placement={'top-end'} title='Go to Team Goals Dashboard'>
            <LBButton
              variant='ghost'
              size='icon'
              onClick={() => {
                redirectToTeamGoals(
                  history,
                  selectedTeam,
                  selectedTeamDashboardId
                );
              }}
            >
              <LBArrowCornerRightUpIcon />
            </LBButton>
          </Tooltip>
        ) : null}

        {withPermissions({
          WrappedComponent: !!slackChannel && !isComingSoon && (
            <Margin l='small'>
              <Tooltip placement='top-end' title='Customize Team Goals'>
                <LBButton
                  variant='ghost'
                  size='icon'
                  onClick={() =>
                    history.push({
                      pathname: '/settings/notifications'
                    })
                  }
                >
                  <Icon name='settings' size='smaller' color='dodgerBlue' />
                </LBButton>
              </Tooltip>
            </Margin>
          ),
          requiredPermissions: CAN_EDIT_ACCOUNT_TYPES
        })}
      </Title>

      <NotificationsContent
        notifications={notifications}
        onRemoveClick={onRemoveClick}
        isLoading={isLoading}
        isComingSoon={isComingSoon}
      />
    </Flex>
  );
};

export default Notifications;
